"use client";
import * as React from "react";
import { FiUser, FiCamera } from "react-icons/fi";
import { useProfile } from "../../context/ProfileContext";
import { UPLOAD_URL } from "../../config/config";

export default function ProfileForm() {
  const {
    profile,
    updateProfile,
    loading,
    setProfile,
  } = useProfile();

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [profileImage, setProfileImage] = React.useState("");
  const [imageFile, setImageFile] = React.useState(null);
  const [isUploading, setIsUploading] = React.useState(false);

  const [status, setStatus] = React.useState(null);
  const fileInputRef = React.useRef(null);

  // Populate profile data
  React.useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || "");
      setLastName(profile.last_name || "");
      if (profile.image) setProfileImage(`${UPLOAD_URL}/users/${profile.image}`);
    }
  }, [profile]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    try {
      const formData = new FormData();
      formData.append("first_name", firstName);
      formData.append("last_name", lastName);
      if (imageFile) formData.append("image", imageFile);

      const result = await updateProfile(formData);

      if (result.success) {
        const updated = result.data;
        setStatus("✅ Profile updated successfully!");
        setImageFile(null);

        // Update form inputs immediately
        setFirstName(updated.first_name || "");
        setLastName(updated.last_name || "");
        if (updated.image) {
          setProfileImage(`${UPLOAD_URL}/users/${updated.image}`);
        }

        setProfile(updated); // ✅ Updates global context
      } else {
        setStatus(`❌ Failed: ${result.message}`);
      }
    } catch (err) {
      setStatus(`❌ Error: ${err.message}`);
    }
  };

  // Image preview
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setIsUploading(true);

      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = () => fileInputRef.current?.click();

  if (loading) return <div className="text-center p-10 text-gray-500">Loading profile...</div>;

  return (
     <main className="ml-4 w-[73%] p-6 max-md:ml-0 max-md:w-full max-sm:w-full">
      <div className="w-full max-md:mt-8 max-md:max-w-full">
        <header className="flex gap-2.5 items-center py-1.5 text-2xl font-bold text-center border-b border-black border-opacity-10 min-h-12 text-sky-950">
          <h2 className="self-stretch my-auto text-sky-950">Edit Profile</h2>
        </header>

        <section className="flex flex-col justify-center items-center px-20 py-11 mt-7 text-base text-black bg-white rounded-2xl shadow-2xl max-md:px-5 max-md:max-w-full">
          <form onSubmit={handleSubmit} className="flex flex-col max-w-full w-[335px]">
            <div className="flex flex-col items-center mb-5">
              <div className="relative group cursor-pointer" onClick={handleImageClick}>
                <img
                  src={profileImage}
                  alt="Profile picture"
                  className="object-contain self-center max-w-full aspect-square w-[100px] rounded-full transition-opacity group-hover:opacity-90"
                />
                {isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  </div>
                )}

                {/* Camera icon overlay at bottom right */}
                <div className="absolute bottom-0 right-0 bg-[#002347] rounded-full p-2 shadow-lg border-2 border-gray-400 hover:bg-gray-50 transition-colors">
                  <FiCamera className="text-white text-lg" />
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                aria-label="Upload profile picture"
              />
            </div>

            <div className="flex flex-col gap-5">
              <div className="flex flex-col">
                <label htmlFor="firstName" className="sr-only">First Name</label>
                <div className="relative flex items-center">
                  <FiUser className="absolute left-0 text-red-500 outline outline-1 outline-red-500 rounded-sm text-lg z-10" aria-hidden="true" />
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only alphabets and spaces
                      if (/^[A-Za-z\s]*$/.test(value)) {
                        setFirstName(value);
                      }
                    }}

                    className="flex gap-3 items-center py-2.5 pl-8 pr-3 w-full font-medium whitespace-nowrap border-b border-solid border-black border-opacity-20 bg-transparent focus:outline-none focus:ring-0 focus:border-b-red-500 transition-colors"
                    placeholder="First Name"
                    required
                    aria-describedby="firstName-error"
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <label htmlFor="lastName" className="sr-only">Last Name</label>
                <div className="relative flex items-center">
                  <FiUser className="absolute left-0 text-red-500 outline outline-1 outline-red-500 rounded-sm text-lg z-10" aria-hidden="true" />
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only alphabets and spaces
                      if (/^[A-Za-z\s]*$/.test(value)) {
                        setLastName(value);
                      }
                    }}
                    className="flex gap-3 items-center py-2.5 pl-8 pr-3 w-full whitespace-nowrap border-b border-black border-opacity-20 bg-transparent focus:outline-none focus:ring-0 focus:border-b-red-500 transition-colors"
                    placeholder="Last Name"
                    required
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="flex gap-10 justify-center items-center self-center px-5 py-4 mt-10 max-w-full text-base font-semibold tracking-wide leading-tight text-center text-white bg-green-500 rounded-2xl border-b border-solid shadow-lg border-b-white border-b-opacity-30 min-h-[50px] w-[295px] hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isUploading}
            >
              <span className="self-stretch my-auto text-white w-[129px]">
                {isUploading ? "Uploading..." : "Save & Update"}
              </span>
            </button>
            {status && (
              <div className="mt-4 text-center text-sm font-medium text-gray-700">{status}</div>
            )}
          </form>
        </section>
      </div>
    </main>
  );
}
